import React, { useEffect, useCallback, useState } from 'react';
import { useSynchronizedCharts } from '../../hooks/useSynchronizedCharts';
import SystemTempChart from './SystemTempChart';
import ComfortTempChart from './ComfortTempChart';
import Co2Chart from './Co2Chart';
import ActivationScheduleChart from './ActivationScheduleChart';
import { Card } from '@mui/material';
import { lightTheme, darkTheme } from './highchartsTheme';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts';

import NoData from 'highcharts/modules/no-data-to-display';

NoData(Highcharts); // init once

export default function SynchronizedHeatingCharts({
  heatingPipeTemp,
  coldWaterTemp,
  comfortTemps,
  co2Series,
  powerConsumptionSeries,
  waterConsumptionSeries,
  relativeHumiditySeries,
  activationRanges,
  baselineRanges,
  occupancyRanges,
  xAxisRange,
  loading = false,
}) {
  const muiTheme = useTheme(); // or receive a prop: themeMode = 'light' | 'dark'
  const mode = muiTheme.palette.mode; // 'light' | 'dark'
  const { containerRef, syncExtremes } = useSynchronizedCharts(mode);

  // NEW: which chart is expanded?
  const [expanded, setExpanded] = useState(null); // 'activation'|'heating'|'cold'|'comfort'|'co2'|null
  const isExpanded = useCallback((id) => expanded === id, [expanded]);
  const toggle = useCallback((id) => {
    setExpanded((curr) => (curr === id ? null : id));
  }, []);

  useEffect(() => {
    const theme = mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(theme);

    Highcharts.charts.forEach((c) => {
      if (!c) return;
      c.update(
        {
          chart: theme.chart,
          title: theme.title,
          xAxis: theme.xAxis,
          yAxis: theme.yAxis,
          legend: theme.legend,
          tooltip: theme.tooltip,
          plotOptions: theme.plotOptions,
        },
        false,
        true,
        false,
      );
      c.redraw();
      c.reflow();
    });
  }, [mode]);

  const commonXAxis = {
    type: 'datetime',
    min: xAxisRange.min,
    max: xAxisRange.max,
    crosshair: true,
    ordinal: false,
    events: { setExtremes: syncExtremes },
  };

  const h = (id) => (isExpanded(id) ? 400 : 200);

  // A tiny helper to make the whole card clickable
  const CardClickable = ({ id, children }) => (
    <Card
      variant="outlined"
      onDoubleClick={() => toggle(id)}
      sx={{
        p: 1,
        cursor: 'pointer',
        // transition: 'transition all 300ms ease',
        ':hover': {
          borderColor: 'divider',
        },
        // transform: isExpanded(id) ? 'scale(1.01)' : 'none',
      }}
    >
      {children}
    </Card>
  );

  return (
    <div
      ref={containerRef}
      id="sync-charts-container"
      style={{ gap: '20px', display: 'flex', flexDirection: 'column' }}
    >
      {activationRanges.length > 0 && (
        <CardClickable id="activation">
          <ActivationScheduleChart
            activationRanges={activationRanges}
            baselineRanges={baselineRanges}
            occupancyRanges={occupancyRanges}
            xAxis={commonXAxis}
            height={h('activation')}
            loading={loading}
          />
        </CardClickable>
      )}

      {heatingPipeTemp.length > 0 && (
        <CardClickable id="heating">
          <SystemTempChart
            title="Heating Pipe Temperatures"
            data={heatingPipeTemp}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('heating')}
            loading={loading}
          />
        </CardClickable>
      )}

      {coldWaterTemp.length > 0 && (
        <CardClickable id="cold">
          <SystemTempChart
            title="Cold Water Temperatures"
            data={coldWaterTemp}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('cold')}
            loading={loading}
          />
        </CardClickable>
      )}

      {comfortTemps.length > 0 && (
        <CardClickable id="comfort">
          <ComfortTempChart
            data={comfortTemps}
            xAxis={commonXAxis}
            colors={['#1976d2', '#d32f2f']}
            height={h('comfort')}
            loading={loading}
          />
        </CardClickable>
      )}

      {co2Series.length > 0 && (
        <CardClickable id="co2">
          <Co2Chart
            data={co2Series}
            xAxis={commonXAxis}
            height={h('co2')}
            loading={loading}
          />
        </CardClickable>
      )}

      {powerConsumptionSeries && powerConsumptionSeries.length > 0 && (
        <CardClickable id="power">
          <SystemTempChart
            title="Power Consumption"
            data={powerConsumptionSeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('power')}
            loading={loading}
          />
        </CardClickable>
      )}

      {relativeHumiditySeries && relativeHumiditySeries.length > 0 && (
        <CardClickable id="humidity">
          <SystemTempChart
            title="Relative Humidity"
            data={relativeHumiditySeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('humidity')}
            loading={loading}
          />
        </CardClickable>
      )}

      {waterConsumptionSeries && waterConsumptionSeries.length > 0 && (
        <CardClickable id="water">
          <SystemTempChart
            title="Water Consumption"
            data={waterConsumptionSeries}
            xAxis={commonXAxis}
            colors={['#1976d2']}
            height={h('water')}
            loading={loading}
          />
        </CardClickable>
      )}
    </div>
  );
}
